import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { ComparisonFileRepresentation, ComparisonFileSort, Direction } from '~/data/openapi-client/index';
import { useInfiniteComparisonFiles } from '~/data/comparisons/queries';
import { useDebounceValue } from '~/app/utils/debounce';
import useIntersectionObserver from '~/app/utils/hooks/useIntersectionObserver';

type ComparisonFileAutocompleteProps = {
  clientId: string;
  value: ComparisonFileRepresentation | null;
  onChange: (value: ComparisonFileRepresentation | null) => void;
  label?: string;
};

const ComparisonFileAutocomplete = ({
  clientId,
  value,
  onChange,
  label = 'Choose file',
}: ComparisonFileAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebounceValue(inputValue);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteComparisonFiles(clientId, {
    search: debouncedSearch || undefined,
    sort: ComparisonFileSort.Inserted,
    direction: Direction.Desc,
  });

  const options = data?.pages.flatMap((page) => page.items) ?? [];

  const lastOptionRef = useRef<HTMLLIElement | null>(null);
  const observer = useIntersectionObserver(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    { threshold: 0.5 },
  );

  const setLastOptionRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (lastOptionRef.current) {
        observer.unobserve(lastOptionRef.current);
      }
      lastOptionRef.current = node;
      if (node) {
        observer.observe(node);
      }
    },
    [observer],
  );

  return (
    <Autocomplete
      fullWidth
      value={value}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(newInputValue);
        }
      }}
      options={options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(x) => x}
      loading={isLoading}
      renderOption={(props, option, { index }) => {
        const isLast = index === options.length - 1;
        return (
          <li {...props} key={option.id} ref={isLast ? setLastOptionRef : undefined}>
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading || isFetchingNextPage ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ComparisonFileAutocomplete;
